import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { OrganizationService, OrganizationInfo, NotifyCustomerToCloseDoorMethod, AuthConfiguration, AuthConfigurationSchema } from '../api/api_pb';
import { useClient } from '../connect_api';
import { S3Service } from '../api/api_pb';
import { AuthMethod } from '../kiosk/v1/auth_pb';
import { create } from '@bufbuild/protobuf';

const EditOrganizationInfo: React.FC = () => {
    const navigate = useNavigate();
    const organizationClient = useClient(OrganizationService);
    const s3Client = useClient(S3Service);
    const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [originalLogoUrl, setOriginalLogoUrl] = useState<string>('');
    const [authConfig, setAuthConfig] = useState<AuthConfiguration | null>(null);

    const fetchOrganizationInfo = useCallback(async () => {
        try {
            const response = await organizationClient.getMyOrganizationInfo({});
            setOrganizationInfo(response.organizationInfo || null);
            if (response.organizationInfo) {
                setOriginalLogoUrl(response.organizationInfo.logoUrl);
            }
            
            // Fetch auth configuration
            try {
                const authResponse = await organizationClient.getAuthConfiguration({});
                setAuthConfig(authResponse.config || null);
            } catch (error) {
                console.error('Failed to fetch auth configuration:', error);
                // If auth config fetch fails, set default config
                const defaultConfig = create(AuthConfigurationSchema, {
                    enabledMethods: [AuthMethod.MOBILE_PIN],
                    venueAccessCodeValidationType: 0,
                    venueAccessCodeLength: 6,
                    venueAccessCodeCharacterType: 0,
                    venueAccessCodeApiUrl: ''
                });
                setAuthConfig(defaultConfig);
            }
        } catch (error) {
            console.error('Failed to fetch organization info:', error);
            toast.error('Failed to load organization info.');
        } finally {
            setLoading(false);
        }
    }, [organizationClient]);

    useEffect(() => {
        fetchOrganizationInfo();
    }, [fetchOrganizationInfo]);


    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const isAuthMethodEnabled = (method: AuthMethod): boolean => {
        return authConfig?.enabledMethods?.includes(method) || false;
    };

    const toggleAuthMethod = (method: AuthMethod, enabled: boolean) => {
        if (!authConfig) return;
        
        const currentMethods = authConfig.enabledMethods || [];
        let newMethods: AuthMethod[];
        
        if (enabled) {
            // Add the method if not already present
            newMethods = currentMethods.includes(method) 
                ? currentMethods 
                : [...currentMethods, method];
        } else {
            // Remove the method
            newMethods = currentMethods.filter(m => m !== method);
        }
        
        setAuthConfig({
            ...authConfig,
            enabledMethods: newMethods
        });
    };

    const handleUpdate = async () => {
        if (!organizationInfo) return;
        
        // Validate at least one auth method is enabled
        if (!authConfig || authConfig.enabledMethods.length === 0) {
            toast.error('At least one authentication method must be enabled');
            return;
        }
        
        try {
            let logoUrl = originalLogoUrl;
            if (imageFile) {
                const extension = imageFile.name.split('.').pop() || '';
                const presignedUrlResponse = await s3Client.getUploadUrl({ extension });
                const presignedUrl = presignedUrlResponse.url;

                const uploadResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    body: imageFile,
                    headers: {
                        'Content-Type': imageFile.type,
                    },
                });

                if (!uploadResponse.ok) {
                    toast.dismiss();
                    toast.error('Failed to upload image, please try again later');
                    throw new Error('Failed to upload image');
                }

                logoUrl = presignedUrl.split('?')[0];
            }

            const updatedOrganizationInfo = {
                ...organizationInfo,
                logoUrl,
            };

            await organizationClient.updateOrganizationInfo({
                organizationInfo: updatedOrganizationInfo,
            });
            
            // Update auth configuration
            if (authConfig) {
                try {
                    await organizationClient.updateAuthConfiguration({
                        config: authConfig,
                    });
                } catch (error) {
                    console.error('Failed to update auth configuration:', error);
                    toast.warning('Organization info updated, but auth configuration update failed.');
                }
            }

            toast.dismiss();
            toast.success('Organization info updated successfully');
            navigate('/');
        } catch (error) {
            toast.dismiss();
            toast.error('Failed to update organization info: ' + (error as Error).message);
            console.error('Error:', error);
        }
    };

    return (
        <div className="p-4">
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div>
                    <h2 className="text-lg font-bold">Edit Organization Info</h2>
                    <div className="mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                            <p>{organizationInfo?.name}</p>
                        </div>
                        <div className="border-b border-gray-300 my-2"></div>
                        <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700">MobileNumber Country Code</label>
                            <input
                                type="text"
                                value={organizationInfo?.mobileNumberCountryCode || ''}
                                onChange={(e) => {
                                    setOrganizationInfo((prev) =>
                                        prev ? { ...prev, mobileNumberCountryCode: e.target.value } : null
                                    );
                                }}
                                className="mt-1 block w-full px-1 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.closeDoorDetection || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, closeDoorDetection: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Enable Close Door Detection</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">*If enabled, the customer cannot return to the homepage in 5 minutes with the door open.</label>
                        </div>


                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.enableDoorNotClosedNotification || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, enableDoorNotClosedNotification: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Enable Door Not Closed Notification</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">
                                If enabled, if the door is not closed in a timely manner, the administrator will be notified
                            </label>
                        </div>


                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.keepReadyOnFaultReport || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, keepReadyOnFaultReport: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Keep Trackable Item Ready</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">* If enabled, trackable items will not be marked as pending inspection after customers report damages.</label>
                        </div>
                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.sendPincodeOnLogin || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, sendPincodeOnLogin: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Send Pin Code On Login</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">
                                If enabled, a pin code will be sent to the customer every time they log in.
                            </label>
                        </div>

                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                               Notify Customer To Close Door
                            </label>
                            <label className="block text-sm font-medium text-gray-700 flex space-x-6">
                                <label className="flex items-center space-x-1 cursor-pointer">
                                    <input type="radio"
                                           name="notifyCustomerToCloseDoor"
                                           checked={organizationInfo?.notifyCustomerToCloseDoorMethod === NotifyCustomerToCloseDoorMethod.NONE || organizationInfo?.notifyCustomerToCloseDoorMethod === NotifyCustomerToCloseDoorMethod.UNSPECIFIED}
                                           onChange={() => {
                                               setOrganizationInfo((prev) =>
                                                   prev ? {
                                                       ...prev,
                                                       notifyCustomerToCloseDoorMethod: NotifyCustomerToCloseDoorMethod.NONE
                                                   } : null
                                               );
                                           }}
                                    />
                                    <span>NONE</span>
                                </label>
                                <label className="flex items-center space-x-1 cursor-pointer">
                                    <input type="radio"
                                           name="notifyCustomerToCloseDoor"
                                           checked={organizationInfo?.notifyCustomerToCloseDoorMethod === NotifyCustomerToCloseDoorMethod.SMS}
                                           onChange={() => {
                                               setOrganizationInfo((prev) =>
                                                   prev ? {
                                                       ...prev,
                                                       notifyCustomerToCloseDoorMethod: NotifyCustomerToCloseDoorMethod.SMS
                                                   } : null
                                               );
                                           }}
                                    />
                                    <span>SMS</span>
                                </label>
                            </label>
                        </div>

                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.skipReturnConditionConfirmation || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, skipReturnConditionConfirmation: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Skip Return Condition Confirmation</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">
                                If enabled, customers will not be asked to confirm item condition during returns (items will be automatically marked as not requiring inspection).
                            </label>
                        </div>

                        <div className="mt-2">
                            <label className="inline-flex items-center mt-1">
                                <input
                                    type="checkbox"
                                    checked={organizationInfo?.notifyCustomerLeaseExpiredSms || false}
                                    onChange={(e) => {
                                        setOrganizationInfo((prev) =>
                                            prev ? { ...prev, notifyCustomerLeaseExpiredSms: e.target.checked } : null
                                        );
                                    }}
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                />
                                <span className="ml-2 text-gray-700">Notify Customer When Lease Expires (SMS)</span>
                            </label>
                            <label className="block text-sm font-medium text-gray-700">
                                If enabled, customers will receive an SMS notification when their rental period expires (item not returned within the lease duration limit). This only applies to rental items, not for-sale items.
                            </label>
                        </div>

                        <div className="border-b border-gray-300 my-2"></div>
                        
                        {/* Authentication Configuration Section */}
                        <div className="mt-4" data-testid="auth-configuration-section">
                            <h3 className="text-md font-semibold text-gray-800 mb-3" data-testid="auth-methods-title">Authentication Methods</h3>
                            <p className="text-sm text-gray-600 mb-3" data-testid="auth-methods-description">Configure available authentication methods for kiosk access</p>
                            
                            {/* Mobile + PIN */}
                            <div className="mt-2">
                                <label className="inline-flex items-center mt-1">
                                    <input
                                        type="checkbox"
                                        checked={isAuthMethodEnabled(AuthMethod.MOBILE_PIN)}
                                        onChange={(e) => {
                                            toggleAuthMethod(AuthMethod.MOBILE_PIN, e.target.checked);
                                        }}
                                        className="form-checkbox h-5 w-5 text-blue-600"
                                        disabled={!authConfig}
                                    />
                                    <span className="ml-2 text-gray-700">Mobile + PIN</span>
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                    Traditional SMS verification with PIN code for secure authentication
                                </label>
                            </div>
                            
                            {/* Venue Access Code */}
                            <div className="mt-2">
                                <label className="inline-flex items-center mt-1">
                                    <input
                                        type="checkbox"
                                        checked={isAuthMethodEnabled(AuthMethod.VENUE_ACCESS_CODE)}
                                        onChange={(e) => {
                                            toggleAuthMethod(AuthMethod.VENUE_ACCESS_CODE, e.target.checked);
                                        }}
                                        className="form-checkbox h-5 w-5 text-blue-600"
                                        disabled={!authConfig}
                                    />
                                    <span className="ml-2 text-gray-700">Venue Access Code</span>
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                    Alternative code-based authentication for venue-specific access
                                </label>
                            </div>
                            
                            {/* Mobile Express */}
                            <div className="mt-2">
                                <label className="inline-flex items-center mt-1">
                                    <input
                                        type="checkbox"
                                        checked={isAuthMethodEnabled(AuthMethod.MOBILE_EXPRESS)}
                                        onChange={(e) => {
                                            toggleAuthMethod(AuthMethod.MOBILE_EXPRESS, e.target.checked);
                                        }}
                                        className="form-checkbox h-5 w-5 text-blue-600"
                                        disabled={!authConfig}
                                    />
                                    <span className="ml-2 text-gray-700">Mobile Express</span>
                                    <span className="ml-2 text-amber-600 text-sm font-medium">⚠️ No Verification</span>
                                </label>
                                <label className="block text-sm font-medium text-gray-700">
                                    Mobile-only authentication without PIN verification
                                </label>
                                {isAuthMethodEnabled(AuthMethod.MOBILE_EXPRESS) && (
                                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded" data-testid="mobile-express-warning">
                                        <p className="text-xs text-amber-800 flex items-center">
                                            <span className="mr-1">⚠️</span>
                                            <strong>Security Warning:</strong> This method has no verification step and relies entirely on customer honesty. Only use in controlled/trusted environments.
                                        </p>
                                    </div>
                                )}
                            </div>
                            
                            <div className="mt-3 p-3 bg-gray-50 rounded">
                                <p className="text-xs text-gray-600">
                                    <strong>Note:</strong> At least one authentication method must be enabled. Changes will take effect immediately for new authentication attempts.
                                </p>
                            </div>
                        </div>

                        <div className="border-b border-gray-300 my-2"></div>
                        <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700">Logo</label>
                            <input type="file" accept="image/*" onChange={handleImageChange} />
                            {(imageUrl || originalLogoUrl) && (
                                <img
                                    src={imageUrl || originalLogoUrl}
                                    alt="Preview"
                                    className="mt-2 w-32 h-32 object-contain"
                                />
                            )}
                        </div>
                    </div>
                    <div className="mt-4">
                        <button onClick={() => navigate('/')} className="bg-gray-300 text-black p-2 rounded-md">Cancel</button>
                        <button onClick={handleUpdate} className="bg-blue-600 text-white p-2 rounded-md ml-2">Update</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditOrganizationInfo;
